import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { getStockItemById } from '@/lib/queries/stockItems'
import { getAllCategories } from '@/lib/queries/categories'
import { stockItemService } from '@/services/stockItemService'

// Form validation schema
// Validation rules match Sanity schema definition
// Reference: sanity/schemas/stockItemType.ts
// 
// Schema validation rules from stockItemType.ts:
// - name: Rule.required().min(1).max(200) (line 17)
// - sku: Rule.required().min(1).max(100) (line 25)
// - description: optional text field (line 32-37)
// - packageDetails.length: Rule.min(0) (line 142)
// - packageDetails.width: Rule.min(0) (line 149)
// - packageDetails.height: Rule.min(0) (line 156)
// - packageDetails.weight: Rule.min(0) (line 163)
// - packageDetails.dimensionUnit: Rule.required() (line 178)
// - packageDetails.weightUnit: Rule.required() (line 193)
//
// Note: supplierItem is NOT editable - it's linked to supplier via supplierType field
// Note: unit field is required in schema but not included in edit form (managed separately)
const stockItemSchema = z.object({
  // name: Rule.required().min(1).max(200) - from sanity/schemas/stockItemType.ts line 17
  name: z.string()
    .min(1, 'Name is required')
    .max(200, 'Name must be 200 characters or less'),
  
  // sku: Rule.required().min(1).max(100) - from sanity/schemas/stockItemType.ts line 25
  sku: z.string()
    .min(1, 'SKU is required')
    .max(100, 'SKU must be 100 characters or less'),
  
  // description: optional text field - from sanity/schemas/stockItemType.ts line 32-37
  description: z.string().optional(),
  
  // category: optional reference - from sanity/schemas/stockItemType.ts line 84-89
  category: z
    .object({
      _ref: z.string(),
      _type: z.literal('reference'),
    })
    .optional(),
  
  // packageDetails: optional object - from sanity/schemas/stockItemType.ts line 131-199
  packageDetails: z.object({
    // length: Rule.min(0) - from sanity/schemas/stockItemType.ts line 142
    length: z.union([
      z.number().min(0, 'Length must be 0 or greater'), 
      z.literal('')
    ]).optional(),
    
    // width: Rule.min(0) - from sanity/schemas/stockItemType.ts line 149
    width: z.union([
      z.number().min(0, 'Width must be 0 or greater'), 
      z.literal('')
    ]).optional(),
    
    // height: Rule.min(0) - from sanity/schemas/stockItemType.ts line 156
    height: z.union([
      z.number().min(0, 'Height must be 0 or greater'), 
      z.literal('')
    ]).optional(),
    
    // weight: Rule.min(0) - from sanity/schemas/stockItemType.ts line 163
    weight: z.union([
      z.number().min(0, 'Weight must be 0 or greater'), 
      z.literal('')
    ]).optional(),
    
    // dimensionUnit: Rule.required() - from sanity/schemas/stockItemType.ts line 178
    // Options: mm, cm, in, m - from sanity/schemas/stockItemType.ts line 171-176
    dimensionUnit: z.enum(['mm', 'cm', 'in', 'm']),
    
    // weightUnit: Rule.required() - from sanity/schemas/stockItemType.ts line 193
    // Options: g, kg, lb, oz - from sanity/schemas/stockItemType.ts line 186-191
    weightUnit: z.enum(['g', 'kg', 'lb', 'oz']),
  }).optional(),
  
  images: z.array(z.string()).optional(),
  attributes: z.record(z.any()).optional(),
})

type StockItemFormData = z.infer<typeof stockItemSchema>

/**
 * Stock Item Edit page
 * Mobile-first design optimized for iPad Mini
 */
export default function StockItemEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: stockItem, isLoading } = useQuery({
    queryKey: ['stockItem', id],
    queryFn: () => getStockItemById(id!),
    enabled: !!id,
  })

  const { data: categories, isLoading: isLoadingCategories, error: categoriesError } = useQuery({
    queryKey: ['categories'],
    queryFn: getAllCategories,
  })

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<StockItemFormData>({
    resolver: zodResolver(stockItemSchema),
    defaultValues: {
      name: '',
      sku: '',
      description: '',
      category: undefined,
      packageDetails: {
        length: '' as any,
        width: '' as any,
        height: '' as any,
        weight: '' as any,
        dimensionUnit: 'mm',
        weightUnit: 'g',
      },
      images: [],
      attributes: {},
    },
  })

  // Reset form when stockItem data loads
  React.useEffect(() => {
    if (stockItem) {
      // Debug: Log package details to see what we're receiving
      console.log('StockItemEdit - stockItem data:', {
        id: stockItem._id,
        packageDetails: stockItem.packageDetails,
        packageDetailsRaw: JSON.stringify(stockItem.packageDetails),
        packageDetailsKeys: stockItem.packageDetails ? Object.keys(stockItem.packageDetails) : null,
        hasPackageDetails: !!stockItem.packageDetails,
        packageDetailsType: typeof stockItem.packageDetails,
        length: stockItem.packageDetails?.length,
        width: stockItem.packageDetails?.width,
        height: stockItem.packageDetails?.height,
        weight: stockItem.packageDetails?.weight,
      })

      // Extract category reference
      let categoryRef: { _ref: string; _type: 'reference' } | undefined = undefined
      if (stockItem.category) {
        console.log('StockItemEdit - Extracting category:', {
          category: stockItem.category,
          categoryType: typeof stockItem.category,
          hasRef: '_ref' in (stockItem.category as any),
          hasId: '_id' in (stockItem.category as any),
          hasType: '_type' in (stockItem.category as any),
          categoryKeys: stockItem.category ? Object.keys(stockItem.category) : null,
        })
        
        if (typeof stockItem.category === 'object') {
          // Prioritize _ref if it exists (from normalization or reference object)
          if ('_ref' in stockItem.category) {
            // It has _ref - use it (might be from normalization or already a reference)
            categoryRef = {
              _ref: (stockItem.category as any)._ref,
              _type: 'reference',
            }
            console.log('StockItemEdit - Using _ref from category:', categoryRef)
          } else if ('_id' in stockItem.category) {
            // It's populated but no _ref - extract the _id
            categoryRef = {
              _ref: (stockItem.category as any)._id,
              _type: 'reference',
            }
            console.log('StockItemEdit - Using _id from populated category:', categoryRef)
          }
        }
      } else {
        console.log('StockItemEdit - No category found in stockItem')
      }

      // Extract package details - handle both undefined and null cases
      let packageDetailsData: {
        length?: number
        width?: number
        height?: number
        weight?: number
        dimensionUnit?: 'mm' | 'cm' | 'in' | 'm'
        weightUnit?: 'g' | 'kg' | 'lb' | 'oz'
      } | undefined = undefined

      if (stockItem.packageDetails) {
        // Always create packageDetailsData structure if packageDetails exists
        // Convert null/undefined to empty string for React Hook Form number inputs
        // React Hook Form number inputs need empty string (not undefined) to display empty fields
        const getNumberValue = (value: any): number | '' => {
          if (value === null || value === undefined) return ''
          const num = Number(value)
          return isNaN(num) ? '' : num
        }
        
        packageDetailsData = {
          length: getNumberValue(stockItem.packageDetails.length) as any,
          width: getNumberValue(stockItem.packageDetails.width) as any,
          height: getNumberValue(stockItem.packageDetails.height) as any,
          weight: getNumberValue(stockItem.packageDetails.weight) as any,
          dimensionUnit: stockItem.packageDetails.dimensionUnit || 'mm',
          weightUnit: stockItem.packageDetails.weightUnit || 'g',
        } as any
        
        console.log('StockItemEdit - extracted packageDetailsData:', {
          raw: stockItem.packageDetails,
          extracted: packageDetailsData,
          lengthType: typeof stockItem.packageDetails.length,
          lengthValue: stockItem.packageDetails.length,
        })
      }

      console.log('StockItemEdit - packageDetailsData to reset:', packageDetailsData)

      // Build reset data with proper structure
      const resetData: any = {
        name: stockItem.name || '',
        sku: stockItem.sku || '',
        description: stockItem.description || '',
        category: categoryRef,
        images: stockItem.images || [],
        attributes: stockItem.attributes || {},
      }

      // Only include packageDetails if we have data, otherwise use default structure
      if (packageDetailsData) {
        resetData.packageDetails = packageDetailsData
      } else {
        // Use default structure to ensure form fields are cleared
        resetData.packageDetails = {
          length: '',
          width: '',
          height: '',
          weight: '',
          dimensionUnit: 'mm',
          weightUnit: 'g',
        }
      }

      console.log('StockItemEdit - resetData:', resetData)

      reset(resetData, {
        keepDefaultValues: false,
      })
    }
  }, [stockItem, reset])

  const updateMutation = useMutation({
    mutationFn: (data: any) => {
      console.log('StockItemEdit - Calling updateStockItem with:', data)
      return stockItemService.updateStockItem(id!, data)
    },
    onSuccess: (result) => {
      console.log('StockItemEdit - Update successful:', result)
      queryClient.invalidateQueries({ queryKey: ['stockItem', id] })
      queryClient.invalidateQueries({ queryKey: ['stockItems'] })
      navigate(`/stock-items/${id}`)
    },
    onError: (error) => {
      console.error('StockItemEdit - Update failed:', error)
    },
  })

  const onSubmit = async (data: StockItemFormData) => {
    try {
      console.log('StockItemEdit - Form submitted with data:', data)
      
      // Convert empty strings to proper values for packageDetails before saving
      const dataToSave: any = {
        name: data.name,
        sku: data.sku,
        description: data.description || undefined,
        // Note: cost field is not in the Sanity schema, so we don't send it
        // If cost needs to be saved, it should be added to the schema first
        // Category is a reference - always include it (null to clear, or reference object to set)
        category: data.category || null,
        images: data.images || [],
        attributes: data.attributes || {},
      }
      
      console.log('StockItemEdit - Category to save:', {
        categoryFromForm: data.category,
        categoryToSave: dataToSave.category,
        categoryType: typeof dataToSave.category,
        categoryIsObject: dataToSave.category && typeof dataToSave.category === 'object',
        categoryRef: dataToSave.category?._ref,
      })

      // Handle packageDetails - convert empty strings to null for Sanity
      if (data.packageDetails) {
        const cleanPackageDetails: any = {}
        
        // Helper function to safely convert to number or null
        const toNumberOrNull = (value: any): number | null => {
          if (value === '' || value === undefined || value === null) return null
          if (typeof value === 'number' && isNaN(value)) return null
          const num = Number(value)
          return isNaN(num) ? null : num
        }
        
        // Always include all fields, converting numbers properly and setting null for empty values
        cleanPackageDetails.length = toNumberOrNull(data.packageDetails.length)
        cleanPackageDetails.width = toNumberOrNull(data.packageDetails.width)
        cleanPackageDetails.height = toNumberOrNull(data.packageDetails.height)
        cleanPackageDetails.weight = toNumberOrNull(data.packageDetails.weight)
        
        // Always include units (they're required fields with defaults)
        // Explicitly check for undefined/null/empty string and use form value or default
        cleanPackageDetails.dimensionUnit = (
          data.packageDetails.dimensionUnit && 
          typeof data.packageDetails.dimensionUnit === 'string' &&
          data.packageDetails.dimensionUnit.trim() !== ''
        ) ? data.packageDetails.dimensionUnit : 'mm'
        
        cleanPackageDetails.weightUnit = (
          data.packageDetails.weightUnit && 
          typeof data.packageDetails.weightUnit === 'string' &&
          data.packageDetails.weightUnit.trim() !== ''
        ) ? data.packageDetails.weightUnit : 'g'
        
        console.log('StockItemEdit - Units from form:', {
          dimensionUnit: data.packageDetails.dimensionUnit,
          weightUnit: data.packageDetails.weightUnit,
          cleanedDimensionUnit: cleanPackageDetails.dimensionUnit,
          cleanedWeightUnit: cleanPackageDetails.weightUnit,
        })
        
        // Always include packageDetails object to ensure structure is maintained
        dataToSave.packageDetails = cleanPackageDetails
        
        console.log('StockItemEdit - Cleaned packageDetails:', cleanPackageDetails)
      }
      
      console.log('StockItemEdit - Data to save:', dataToSave)
      console.log('StockItemEdit - PackageDetails to save:', dataToSave.packageDetails)
      
      await updateMutation.mutateAsync(dataToSave)
    } catch (error) {
      console.error('Failed to update stock item:', error)
      // Error is handled by React Query mutation
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (!stockItem) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Stock item not found</p>
          <button
            onClick={() => navigate('/stock-items')}
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium"
          >
            Back to List
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Edit Stock Item</h1>
          <button
            onClick={() => navigate(`/stock-items/${id}`)}
            className="text-blue-600 font-medium touch-target-min-44"
          >
            Cancel
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Basic Information Card */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

            <div className="space-y-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  {...register('name')}
                  type="text"
                  id="name"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* SKU */}
              <div>
                <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-1">
                  SKU *
                </label>
                <input
                  {...register('sku')}
                  type="text"
                  id="sku"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.sku && (
                  <p className="mt-1 text-sm text-red-600">{errors.sku.message}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Description
                </label>
                <textarea
                  {...register('description')}
                  id="description"
                  rows={4}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Category Type */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                {isLoadingCategories ? (
                  <div className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base bg-gray-50 text-gray-500">
                    Loading categories...
                  </div>
                ) : categoriesError ? (
                  <div className="w-full px-3 py-3 border border-red-300 rounded-lg text-base bg-red-50 text-red-600">
                    Error loading categories
                  </div>
                ) : (
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => {
                      // Get the current category ID from field value
                      // field.value should be set by reset() in useEffect
                      const currentCategoryId = field.value?._ref || ''
                      
                      return (
                        <select
                          id="category"
                          value={currentCategoryId}
                          onChange={(e) => {
                            field.onChange(
                              e.target.value
                                ? { _ref: e.target.value, _type: 'reference' as const }
                                : undefined
                            )
                          }}
                          onBlur={field.onBlur}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select category</option>
                          {categories && categories.length > 0 ? (
                            categories.map((ct) => (
                              <option key={ct._id} value={ct._id}>
                                {ct.name}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              No categories available
                            </option>
                          )}
                        </select>
                      )
                    }}
                  />
                )}
                {categories && categories.length === 0 && !isLoadingCategories && (
                  <p className="mt-1 text-sm text-gray-500">
                    No categories found. Please create categories in Sanity first.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Package Details Card */}
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Package Details</h2>

            <div className="space-y-4">
              {/* Dimension Unit */}
              <div>
                <label htmlFor="dimensionUnit" className="block text-sm font-medium text-gray-700 mb-1">
                  Dimension Unit
                </label>
                <select
                  {...register('packageDetails.dimensionUnit')}
                  id="dimensionUnit"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="mm">Millimeters (mm)</option>
                  <option value="cm">Centimeters (cm)</option>
                  <option value="in">Inches (in)</option>
                  <option value="m">Meters (m)</option>
                </select>
              </div>

              {/* Package Dimensions */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="packageLength" className="block text-sm font-medium text-gray-700 mb-1">
                    Length
                  </label>
                  <input
                    {...register('packageDetails.length', { 
                      setValueAs: (v) => {
                        if (v === '' || v === null || v === undefined) return ''
                        const num = Number(v)
                        return isNaN(num) ? '' : num
                      }
                    })}
                    type="number"
                    step="0.1"
                    id="packageLength"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.packageDetails?.length && (
                    <p className="mt-1 text-sm text-red-600">{errors.packageDetails.length.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="packageWidth" className="block text-sm font-medium text-gray-700 mb-1">
                    Width
                  </label>
                  <input
                    {...register('packageDetails.width', { 
                      setValueAs: (v) => {
                        if (v === '' || v === null || v === undefined) return ''
                        const num = Number(v)
                        return isNaN(num) ? '' : num
                      }
                    })}
                    type="number"
                    step="0.1"
                    id="packageWidth"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.packageDetails?.width && (
                    <p className="mt-1 text-sm text-red-600">{errors.packageDetails.width.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="packageHeight" className="block text-sm font-medium text-gray-700 mb-1">
                    Height
                  </label>
                  <input
                    {...register('packageDetails.height', { 
                      setValueAs: (v) => {
                        if (v === '' || v === null || v === undefined) return ''
                        const num = Number(v)
                        return isNaN(num) ? '' : num
                      }
                    })}
                    type="number"
                    step="0.1"
                    id="packageHeight"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.packageDetails?.height && (
                    <p className="mt-1 text-sm text-red-600">{errors.packageDetails.height.message}</p>
                  )}
                </div>
              </div>

              {/* Weight Unit */}
              <div>
                <label htmlFor="weightUnit" className="block text-sm font-medium text-gray-700 mb-1">
                  Weight Unit
                </label>
                <select
                  {...register('packageDetails.weightUnit')}
                  id="weightUnit"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="g">Grams (g)</option>
                  <option value="kg">Kilograms (kg)</option>
                  <option value="lb">Pounds (lb)</option>
                  <option value="oz">Ounces (oz)</option>
                </select>
              </div>

              {/* Package Weight */}
              <div>
                <label htmlFor="packageWeight" className="block text-sm font-medium text-gray-700 mb-1">
                  Weight
                </label>
                <input
                  {...register('packageDetails.weight', { 
                    setValueAs: (v) => {
                      if (v === '' || v === null || v === undefined) return ''
                      const num = Number(v)
                      return isNaN(num) ? '' : num
                    }
                  })}
                  type="number"
                  step="0.01"
                  id="packageWeight"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base touch-target-min-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.packageDetails?.weight && (
                  <p className="mt-1 text-sm text-red-600">{errors.packageDetails.weight.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Supplier Stock Item Card - Read Only */}
          {stockItem.supplierItem && (() => {
            // Check if supplierItem is populated (not just a reference)
            const supplierItem = stockItem.supplierItem && typeof stockItem.supplierItem === 'object' && !('_ref' in stockItem.supplierItem)
              ? stockItem.supplierItem
              : null
            
            if (!supplierItem) {
              return (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Supplier Stock Item</h2>
                  <p className="text-sm text-gray-500">
                    Supplier stock item reference exists but details are not available.
                  </p>
                </div>
              )
            }
            
            // Helper to get category name
            const getCategoryName = (category: any): string | undefined => {
              if (!category || typeof category !== 'object') return undefined
              if ('_ref' in category && !('name' in category)) return undefined
              if ('name' in category) return category.name
              return undefined
            }
            
            // Helper to get unit name
            const getUnitName = (unit: any): string | undefined => {
              if (!unit || typeof unit !== 'object') return undefined
              if ('_ref' in unit && !('name' in unit)) return undefined
              if ('name' in unit) return unit.name
              return undefined
            }
            
            return (
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Supplier Stock Item</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Supplier stock item is linked to supplier via supplierType field and cannot be edited here.
                </p>
                <div className="space-y-3">
                  {/* Basic Information */}
                  {supplierItem.name && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Item Name</div>
                      <div className="text-base text-gray-900">{supplierItem.name}</div>
                    </div>
                  )}
                  
                  {supplierItem.sku && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">SKU</div>
                      <div className="text-base text-gray-900">{supplierItem.sku}</div>
                    </div>
                  )}
                  
                  {supplierItem.description && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Description</div>
                      <div className="text-base text-gray-900">{supplierItem.description}</div>
                    </div>
                  )}
                  
                  {/* Category */}
                  {getCategoryName(supplierItem.category) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Category</div>
                      <div className="text-base text-gray-900">{getCategoryName(supplierItem.category)}</div>
                    </div>
                  )}
                  
                  {/* Unit */}
                  {getUnitName(supplierItem.unit) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Unit</div>
                      <div className="text-base text-gray-900">{getUnitName(supplierItem.unit)}</div>
                    </div>
                  )}
                  
                  {/* Dimensions */}
                  {(supplierItem.length || supplierItem.width) && (
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Dimensions</div>
                      <div className="text-base text-gray-900">
                        {supplierItem.length && supplierItem.width
                          ? `${supplierItem.length} × ${supplierItem.width} mm`
                          : supplierItem.length
                          ? `${supplierItem.length} mm`
                          : supplierItem.width
                          ? `${supplierItem.width} mm`
                          : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 pb-24">
            <button
              type="button"
              onClick={() => navigate(`/stock-items/${id}`)}
              className="flex-1 bg-gray-100 text-gray-900 py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </main>
    </div>
  )
}
